import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { getPhotoCache } from 'common/infrastructure/photo-management/upload.cache';
import { CachedPhotoData, CustomFile } from 'common/infrastructure/photo-management/types';
import { Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';

export interface PhotoUploadParams {
  assetId: Tp3Id;
  assetClassId: Tp3Id;
  tp2AssetId?: number;
  file: File;
}

interface PhotoQueryData {
  data: Array<{
    name: string;
    id: Tp3Id;
    uploadStatus?: string;
    base64Data?: string;
  }>;
}

export const cachedPhotoToCustomFile = (cachedPhoto: CachedPhotoData): CustomFile => {
  const { base64Data } = cachedPhoto;
  const byteString = atob(base64Data);
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(arrayBuffer);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([arrayBuffer], { type: cachedPhoto.type });

  const file = new File([blob], cachedPhoto.name, {
    type: cachedPhoto.type,
  }) as CustomFile;

  file.preview = `data:${cachedPhoto.type};base64,${cachedPhoto.base64Data}`;

  Object.defineProperty(file, 'uploadStatus', {
    value: cachedPhoto.uploadStatus,
    writable: true,
  });

  Object.defineProperty(file, 'photoId', {
    value: cachedPhoto.id,
    writable: true,
  });

  Object.defineProperty(file, 'linkStatus', {
    value: cachedPhoto.linkStatus,
    writable: true,
  });

  return file;
};

export const useAllAssetPhotos = (assetClassId: Tp3Id, assetId: Tp3Id) => {
  const queryClient = useQueryClient();

  const prefetchOfflinePhotos = useCallback(async () => {
    const offlinePhotos = await getPhotoCache(assetId);
    if (offlinePhotos.length > 0) {
      const queryKey = ['getAllPhotosByAsset', assetId, assetClassId];
      const currentData = queryClient.getQueryData<PhotoQueryData>(queryKey) || { data: [] };

      // Deduplicate: filter out offline photos that already exist in currentData
      const existingIds = new Set(currentData.data.map((photo) => photo.id));
      const newOfflinePhotos = offlinePhotos
        .filter((photo) => !existingIds.has(photo.id))
        .map((photo) => ({
          name: photo.name,
          id: photo.id,
          uploadStatus: photo.uploadStatus,
          base64Data: photo.base64Data,
        }));

      if (newOfflinePhotos.length > 0) {
        const combinedData = {
          ...currentData,
          data: [...currentData.data, ...newOfflinePhotos],
        };
        queryClient.setQueryData(queryKey, combinedData);
      }
    }
  }, [assetId, assetClassId, queryClient]);

  // Execute prefetch in useEffect to avoid calling async function during render
  useEffect(() => {
    prefetchOfflinePhotos();
  }, [prefetchOfflinePhotos]);

  return {
    prefetchOfflinePhotos,
  };
};

export const waitForAllPhotoUploads = async (
  assetId: Tp3Id,
  maxWaitTime = 30000
): Promise<boolean> => {
  const startTime = Date.now();
  const pollInterval = 500;

  loggerService.log(LoggerLevel.DEBUG, 'Waiting for photo uploads to complete', {
    assetId,
    maxWaitTime,
  });

  while (Date.now() - startTime < maxWaitTime) {
    const offlinePhotos = await getPhotoCache(assetId);
    const pendingUploads = offlinePhotos.filter(
      (photo) => photo.uploadStatus === 'pending' || photo.uploadStatus === 'uploading'
    );
    const successfulUploads = offlinePhotos.filter((photo) => photo.uploadStatus === 'success');
    const failedUploads = offlinePhotos.filter((photo) => photo.uploadStatus === 'error');

    if (pendingUploads.length === 0) {
      loggerService.log(LoggerLevel.DEBUG, 'All photo uploads completed', {
        assetId,
        totalPhotos: offlinePhotos.length,
        successfulUploads: successfulUploads.length,
        failedUploads: failedUploads.length,
        waitTime: Date.now() - startTime,
      });
      return true;
    }

    loggerService.log(LoggerLevel.DEBUG, 'Still waiting for photo uploads', {
      assetId,
      pendingCount: pendingUploads.length,
      successfulCount: successfulUploads.length,
      failedCount: failedUploads.length,
      totalPhotos: offlinePhotos.length,
      elapsedTime: Date.now() - startTime,
    });

    await new Promise((resolve) => setTimeout(resolve, pollInterval));
  }

  const remainingPhotos = await getPhotoCache(assetId);
  const stillPending = remainingPhotos.filter(
    (photo) => photo.uploadStatus === 'pending' || photo.uploadStatus === 'uploading'
  );
  const finalSuccessful = remainingPhotos.filter((photo) => photo.uploadStatus === 'success');
  const finalFailed = remainingPhotos.filter((photo) => photo.uploadStatus === 'error');

  loggerService.log(LoggerLevel.WARN, 'Photo upload wait timeout', {
    assetId,
    maxWaitTime,
    stillPendingCount: stillPending.length,
    successfulCount: finalSuccessful.length,
    failedCount: finalFailed.length,
    totalPhotos: remainingPhotos.length,
  });

  return false;
};
